import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from "react";
import { submitLead, capturePartial } from "../lib/leadSubmit";
import { trackCustom } from "../lib/eventBus";
import { newEventId } from "../lib/tracking";

/**
 * Mortgage qualifier - presented as a scripted chat with Dorian. Same 3
 * qualifying questions + 3 contact steps as the old form; identical scoring,
 * identical `submitLead` payload. Only the presentation is a bubble thread
 * with quick-reply chips and typed steps. On the done screen the visitor
 * gets a device-adaptive handoff: mobile → primary "Text Dorian" (sms:
 * deeplink), desktop → primary "Call Dorian" (tel:); the other one shows
 * below as a secondary link.
 *
 * Signals extracted (unchanged from the 6-step form):
 *   1. down       - minimum down payment available?
 *   2. employment - guaranteed-hours employee, or 2yrs+ self-employed?
 *   3. rejected   - ever turned down by a bank?
 * Then: name (first + last) → email → phone + CASL consent + submit.
 */

// Dorian's direct number for the handoff CTAs. Placeholder - swap for the
// real Alberta number (or set REACT_APP_DORIAN_PHONE in Cloudflare Pages).
const DORIAN_PHONE =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_DORIAN_PHONE) ||
  "+15875550100";
const DORIAN_PHONE_DISPLAY = DORIAN_PHONE.replace(/^\+1(\d{3})(\d{3})(\d{4})$/, "($1) $2-$3");

const CONSENT_VERSION = "mmg-2026-09";
const CONSENT_TEXT =
  "Yes, text and email me about my mortgage. I agree to receive messages from " +
  "Dorian Reeves, mortgage associate with MMG Mortgages (dorian@mmgmortgages.ca), " +
  "about this enquiry. You can withdraw consent any time: reply STOP to any text, " +
  "or click unsubscribe in any email. Standard message rates may apply. This isn't " +
  "a condition of getting help.";

// Conversational beats. Kept short: every extra second before the next input costs completions.
const TYPING_MS = 250;   // typing dots before each later bot line
const ADVANCE_MS = 150;  // pause between a chip tap and the next question

// ── Scoring - 3 factors, each worth up to 3 points (max 9) ──────────────────
const W = {
  // Chat chips ask in dollars, so they get their own keys: the old 6-step
  // ready20/readyMin/notYet meant percentages, and reusing them would make
  // Dorian's email say "20% or more" for someone who picked "$100k or more".
  // Same weights, so score/tier/Purchase value stay continuous.
  down:       { dp100k: 3, dp25to100k: 2, dpUnder25k: 0, ready20: 3, readyMin: 2, notYet: 0 },
  employment: { guaranteedHours: 3, selfEmployed2y: 3, selfEmployedUnder2y: 1, variable: 1 },
  rejected:   { never: 3, once: 2, multiple: 1, notApplied: 3 },
};
const SCORE_MAX = 9;

function computeScore(a) {
  let s = 0;
  for (const k of Object.keys(W)) if (W[k][a[k]] !== undefined) s += W[k][a[k]];
  return s;
}
function computeTier(a) {
  const s = computeScore(a);
  if (s >= 7) return { t: "HOT", route: "Dorian texts today" };
  if (s >= 4) return { t: "WARM", route: "Dorian texts within 1 business day" };
  return { t: "EARLY", route: "90-day plan sequence" };
}

// Scripted questions - bot bubble text + chip choices. Copy stays in the
// first person; each chip has a short chip label + a longer user-bubble
// echo so the chat reads like a real reply, not just "ready20".
const Q_DOWN = {
  key: "down",
  bot: "First one: how much do you have saved for a down payment? Family gifts and your RRSP count.",
  chips: [
    { v: "dp100k", chip: "$100k or more", echo: "I've got $100,000 or more saved." },
    { v: "dp25to100k", chip: "$25k to $100k", echo: "Somewhere between $25,000 and $100,000." },
    { v: "dpUnder25k", chip: "Under $25k, still saving", echo: "Under $25,000, still saving." },
  ],
};
const Q_EMPLOYMENT = {
  key: "employment",
  bot: "Next - how's your work situation? Lenders want steady, predictable income. A T4 with guaranteed hours or two-plus years self-employed both qualify easily.",
  chips: [
    { v: "guaranteedHours", chip: "Employee, guaranteed hours", echo: "I'm an employee with guaranteed hours." },
    { v: "selfEmployed2y", chip: "Self-employed 2+ years", echo: "Self-employed, 2+ years." },
    { v: "selfEmployedUnder2y", chip: "Self-employed under 2 years", echo: "Self-employed, under 2 years." },
    { v: "variable", chip: "Hourly / commission / variable", echo: "Hourly, commission, or hours vary." },
  ],
};
const Q_REJECTED = {
  key: "rejected",
  bot: "Straight question, no judgment: has a bank ever turned you down for a mortgage? A bank decline usually just means the wrong lender was asked. I work with dozens.",
  chips: [
    { v: "never", chip: "No, never declined", echo: "No, never declined." },
    { v: "once", chip: "Yes, once", echo: "Yes, once." },
    { v: "multiple", chip: "Yes, more than once", echo: "Yes, more than once." },
    { v: "notApplied", chip: "Haven't applied yet", echo: "Haven't applied anywhere yet." },
  ],
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;

function normalizePhone(v) {
  const d = (v || "").replace(/\D/g, "");
  const ten = d.length === 11 && d[0] === "1" ? d.slice(1) : d;
  return ten.length === 10 ? "+1" + ten : null;
}

function doneCopy(name, tierT) {
  if (tierT === "HOT")
    return `Nice one, ${name}. This is exactly the profile I want to look at properly. Written breakdown is on its way to your email. Want to reach me directly right now?`;
  if (tierT === "WARM")
    return `Got it, ${name}. I'll be in touch personally, usually within one business day. The written version lands in your email. Want to reach me directly right now?`;
  return `That's it, ${name}. You're a bit early, so instead of a pitch you'll get the specific list of things to line up so that next time, you walk in already approved. Want to reach me directly right now?`;
}

/**
 * A message in the chat thread. Bot messages render on the left with an
 * avatar dot beside the first message in a run; user messages render on
 * the right in navy. Some messages carry chips or a typed input beneath.
 * kind: "bot" | "user" | "typing"
 */
function Bubble({ kind, text, showAvatar }) {
  if (kind === "typing") {
    return (
      <div className="mq-row bot">
        <div className="mq-av-slot">{showAvatar && <BotAvatar />}</div>
        <div className="mq-bubble bot mq-typing" aria-label="Dorian is typing">
          <span /><span /><span />
        </div>
      </div>
    );
  }
  const bot = kind === "bot";
  return (
    <div className={`mq-row ${bot ? "bot" : "user"}`}>
      {bot && <div className="mq-av-slot">{showAvatar && <BotAvatar />}</div>}
      <div className={`mq-bubble ${bot ? "bot" : "user"}`} role={bot ? "status" : undefined}>
        {text}
      </div>
    </div>
  );
}

function BotAvatar() {
  return (
    <div className="mq-avatar-sm" aria-hidden="true">
      <img src="/assets/dorian-headshot-144.webp" alt="" />
    </div>
  );
}

export default function MortgageQualifier({ source = "mortgages", formName = "mq-chat", onEngage, onDone }) {
  // Chat thread - an ordered list of rendered messages. We rebuild it
  // deterministically from the answers/contact state so a re-render never
  // duplicates a bubble.
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ fname: "", lname: "", phone: "", email: "" });
  const [consentChecked, setConsentChecked] = useState(false);
  const [honeypot, setHoneypot] = useState("");

  // Which step of the script is currently active - drives what the bot
  // says next and what input widget renders below the thread.
  //   q1 → q2 → q3 → name → email → phone → done
  // Starts on q1 with no typing indicator: the intro and the first question appear
  // together immediately, so the first chips are tappable as soon as the page is.
  const [step, setStep] = useState("q1");
  const [typing, setTyping] = useState(false);
  const [formErr, setFormErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Device detection for handoff CTA ordering. Recomputed on mount so an
  // orientation change or DevTools resize doesn't flip mid-chat.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    setIsMobile(window.matchMedia("(max-width: 767px)").matches);
  }, []);

  const startedAt = useRef(Date.now());
  const scrollRef = useRef(null);
  const engagedRef = useRef(false);
  // Enter on the phone field bypasses the disabled button; a second submit mints
  // a new event_id, i.e. a second lead and a second Meta/DomainEx Purchase.
  const submittingRef = useRef(false);
  // One partial record per chat session - the backend upserts on this id.
  const partialIdRef = useRef(null);

  const fireEngage = useCallback(() => {
    if (engagedRef.current) return;
    engagedRef.current = true;
    try { onEngage && onEngage(); } catch { /* non-blocking */ }
  }, [onEngage]);

  const fireDone = useCallback(() => {
    try { onDone && onDone(); } catch { /* non-blocking */ }
  }, [onDone]);

  // ── Thread scripting ──────────────────────────────────────────────────
  // Each entry is a rendered bubble. We derive it every render from state
  // so it's cheap and consistent. Typing indicators only render for the
  // in-flight bot message (during the artificial delay before the next
  // question) - never for past turns.
  const messages = [];

  // Opening bot bubble
  messages.push({ kind: "bot", text: "Hey, it's Dorian. A few quick questions so I can give you a straight answer." });

  const seq = ["q1", "q2", "q3", "name", "email", "phone"];
  const qByStep = { q1: Q_DOWN, q2: Q_EMPLOYMENT, q3: Q_REJECTED };
  const answered = (s) => {
    if (s === "q1") return !!answers.down;
    if (s === "q2") return !!answers.employment;
    if (s === "q3") return !!answers.rejected;
    // Typed contact steps: only echo the user bubble AFTER the visitor has
    // hit Send / Enter and the flow has moved on. Echoing on-validity makes
    // the email fly into the thread while they're still typing it.
    const passed = step === "done" || seq.indexOf(step) > seq.indexOf(s);
    if (s === "name") return passed && !!contact.fname.trim();
    if (s === "email") return passed && EMAIL_RE.test(contact.email.trim());
    if (s === "phone") return step === "done";
    return false;
  };

  for (const s of seq) {
    // The bot line for this step (rendered as long as we've reached it OR
    // any step past it)
    const stepIdx = seq.indexOf(s);
    const currentIdx = step === "done" ? seq.length : seq.indexOf(step);
    if (currentIdx < stepIdx) break; // haven't reached this step yet

    if (s === "q1" || s === "q2" || s === "q3") {
      messages.push({ kind: "bot", text: qByStep[s].bot });
      if (answered(s)) {
        const chip = qByStep[s].chips.find((c) => c.v === answers[qByStep[s].key]);
        if (chip) messages.push({ kind: "user", text: chip.echo });
      }
    } else if (s === "name") {
      messages.push({ kind: "bot", text: "Nice. What's your name? First name is enough to get started, last name helps if I need to look you up with a lender." });
      if (answered(s)) {
        const full = `${contact.fname.trim()} ${contact.lname.trim()}`.trim();
        messages.push({ kind: "user", text: full });
      }
    } else if (s === "email") {
      const fn = contact.fname.trim() || "there";
      messages.push({ kind: "bot", text: `Thanks, ${fn}. Where should the written breakdown go? Programs you qualify for, the numbers, next steps - kept in your inbox for reference.` });
      if (answered(s)) messages.push({ kind: "user", text: contact.email.trim().toLowerCase() });
    } else if (s === "phone") {
      messages.push({ kind: "bot", text: "Last bit - where can I text you? A number that can receive texts, usually your mobile. I'll be in touch shortly from a real Alberta number." });
      if (step === "done") {
        messages.push({ kind: "user", text: contact.phone.trim() });
      }
    }
  }

  // Done bubble
  if (step === "done") {
    const t = computeTier(answers);
    messages.push({ kind: "bot", text: doneCopy(contact.fname.trim() || "there", t.t) });
  }

  // Typing indicator - shown while `typing` is true AND we're not on done.
  // Placed at the end of the thread so it looks like the next bot line is
  // about to arrive.
  if (typing && step !== "done") {
    messages.push({ kind: "typing" });
  }

  // Auto-scroll to bottom on any thread change
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // requestAnimationFrame lets the new bubble mount first so scrollHeight is right
    requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  }, [messages.length, typing, step]);

  // After the first step, each new bot line gets a short typing beat (keeps the chat feel)
  // before its input appears. useLayoutEffect (not useEffect) so setTyping(true) commits
  // BEFORE paint - otherwise there's a one-frame render where the new step's bot bubble and
  // input flash before the effect flips typing back on, which loses focus and eats an Enter
  // keystroke pressed in that gap. The first step is skipped (idempotent under StrictMode).
  const instantStepRef = useRef("q1");
  useLayoutEffect(() => {
    if (step === instantStepRef.current || step === "done") return;
    setTyping(true);
    const t = setTimeout(() => setTyping(false), TYPING_MS);
    return () => clearTimeout(t);
  }, [step]);

  // ── Actions ───────────────────────────────────────────────────────────
  const chooseChip = (stepId, key, v) => {
    fireEngage();
    const next = { ...answers, [key]: v };
    setAnswers(next);
    trackCustom("MQ_Answer", { q: key, a: v });
    if (stepId === "q3") trackCustom("MQ_Qualified", { tier: computeTier(next).t, score: computeScore(next) });
    const nextStep = { q1: "q2", q2: "q3", q3: "name" }[stepId];
    setTimeout(() => setStep(nextStep), ADVANCE_MS);
  };

  const submitName = () => {
    fireEngage();
    if (!contact.fname.trim()) return setFormErr("Just need a first name so I know who I'm texting.");
    setFormErr("");
    setStep("email");
  };
  const submitEmail = () => {
    fireEngage();
    if (!EMAIL_RE.test(contact.email.trim())) return setFormErr("That email looks off. Double-check it so the written breakdown reaches you.");
    setFormErr("");
    // Partial-lead capture: if they stop at the phone step, Dorian sends one
    // check-in email later. Skipped for bots (honeypot) - the server re-checks.
    if (!partialIdRef.current) partialIdRef.current = newEventId();
    capturePartial({
      partialId: partialIdRef.current,
      source,
      formName,
      email: contact.email.trim().toLowerCase(),
      name: `${contact.fname.trim()} ${contact.lname.trim()}`.trim(),
      fields: { ...answers, score: computeScore(answers), score_max: SCORE_MAX, tier: computeTier(answers).t },
      honeypot,
    });
    trackCustom("MQ_Partial", { tier: computeTier(answers).t });
    setStep("phone");
  };
  const submitPhone = async () => {
    if (submittingRef.current) return;
    fireEngage();
    setFormErr("");
    if (honeypot) { setStep("done"); fireDone(); return; }
    const phone = normalizePhone(contact.phone);
    if (!phone) return setFormErr("That phone number doesn't look like a 10-digit Canadian number. Mind checking it?");
    if (!consentChecked) return setFormErr("We need your tick on the box above before we're allowed to text or email you. Canadian law, not us being difficult.");

    const t = computeTier(answers);
    const score = computeScore(answers);
    submittingRef.current = true;
    setSubmitting(true);
    let sent = false;
    try {
      await submitLead({
        source,
        formName,
        fields: {
          ...answers,
          score, score_max: SCORE_MAX, tier: t.t, route: t.route,
          seconds_on_form: Math.round((Date.now() - startedAt.current) / 1000),
        },
        name: `${contact.fname.trim()} ${contact.lname.trim()}`.trim(),
        email: contact.email.trim().toLowerCase(),
        phone,
        honeypot,
        metaEventName: "Purchase",
        busEventName: "submit_application",
        purchaseValue: score,
        consent: {
          accepted: consentChecked, sms: consentChecked, email: consentChecked,
          text: CONSENT_TEXT, text_version: CONSENT_VERSION, text_excerpt: CONSENT_TEXT.slice(0, 140),
          timestamp: new Date().toISOString(),
          page_url: typeof window !== "undefined" ? window.location.href : null,
        },
      });
      sent = true;
      trackCustom("MQ_Lead_" + t.t, { score });
      setStep("done");
      fireDone();
    } catch {
      setFormErr(`Something went wrong sending that. Give it another tap. If it keeps failing, text READY to ${DORIAN_PHONE_DISPLAY}.`);
    } finally {
      // Stay locked after success; only a failed send may be retried.
      if (!sent) submittingRef.current = false;
      setSubmitting(false);
    }
  };

  // Track which bot message needs an avatar - first bot bubble in a run of
  // consecutive bot messages gets one, the rest are visually attached.
  const withAvatars = messages.map((m, i) => {
    if (m.kind !== "bot" && m.kind !== "typing") return m;
    const prev = messages[i - 1];
    const showAvatar = !prev || (prev.kind !== "bot" && prev.kind !== "typing");
    return { ...m, showAvatar };
  });

  // ── Bottom input widget - depends on active step ──────────────────────
  const chipsFor = step === "q1" ? Q_DOWN : step === "q2" ? Q_EMPLOYMENT : step === "q3" ? Q_REJECTED : null;
  const firstName = contact.fname.trim() || "there";
  const smsBody = encodeURIComponent(`Hey Dorian, I just filled out your online form. My name is ${firstName}. Talk soon.`);
  const smsHref = `sms:${DORIAN_PHONE}?&body=${smsBody}`;
  const telHref = `tel:${DORIAN_PHONE}`;

  return (
    <div className="mq">
      <div className="mq-chat">
        {/* Chat header - sits on the card, no page-level border */}
        <div className="mq-chat-head">
          <BotAvatar />
          <div className="mq-chat-name">
            <b>Dorian Reeves</b>
            <span>Reads every answer personally</span>
          </div>
        </div>

        {/* Honeypot - off-screen, catches bots */}
        <input className="mq-hp" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true"
          value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />

        {/* Scrollable thread */}
        <div className="mq-chat-scroll" ref={scrollRef}>
          {withAvatars.map((m, i) => (
            <Bubble key={i} kind={m.kind} text={m.text} showAvatar={m.showAvatar} />
          ))}
        </div>

        {/* Bottom input area - swaps by step */}
        <div className="mq-chat-input">
          {formErr && <div className="mq-err" role="alert">{formErr}</div>}

          {!typing && chipsFor && (
            <div className="mq-chips">
              {chipsFor.chips.map((c) => (
                <button
                  key={c.v}
                  type="button"
                  className="mq-chip"
                  onClick={() => chooseChip(step, chipsFor.key, c.v)}
                  data-testid={`mq-${chipsFor.key}-${c.v}`}
                >
                  {c.chip}
                </button>
              ))}
            </div>
          )}

          {!typing && step === "name" && (
            <div className="mq-typed">
              <div className="mq-grid2">
                <input type="text" autoComplete="given-name" placeholder="First name" value={contact.fname}
                  autoFocus
                  onFocus={fireEngage}
                  onChange={(e) => setContact((c) => ({ ...c, fname: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && submitName()}
                  data-testid="mq-fname" />
                <input type="text" autoComplete="family-name" placeholder="Last name" value={contact.lname}
                  onChange={(e) => setContact((c) => ({ ...c, lname: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && submitName()} />
              </div>
              <button className="mq-btn" type="button" onClick={submitName} data-testid="mq-name-continue">Send →</button>
            </div>
          )}

          {!typing && step === "email" && (
            <div className="mq-typed">
              <input type="email" autoComplete="email" inputMode="email" placeholder="dana@email.com" value={contact.email}
                autoFocus
                onFocus={fireEngage}
                onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && submitEmail()}
                data-testid="mq-email" />
              <button className="mq-btn" type="button" onClick={submitEmail} data-testid="mq-email-continue">Send →</button>
              <p className="mq-legal">
                If you don't finish, I may send you one email to check in. Unsubscribe any time.
              </p>
            </div>
          )}

          {!typing && step === "phone" && (
            <div className="mq-typed">
              <input type="tel" autoComplete="tel" inputMode="tel" placeholder="(403) 555-0123" value={contact.phone}
                autoFocus
                onFocus={fireEngage}
                onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && submitPhone()}
                data-testid="mq-phone" />
              <label className="mq-consent" htmlFor="mq-casl">
                <input id="mq-casl" type="checkbox" checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)} data-testid="mq-consent" />
                <span className="txt">
                  <b>Yes, text and email me about my mortgage.</b> Messages from Dorian Reeves (MMG Mortgages,
                  dorian@mmgmortgages.ca) about this enquiry. Reply STOP to any text or unsubscribe in any
                  email to opt out. Not a condition of getting help.
                </span>
              </label>
              <button className="mq-btn" type="button" onClick={submitPhone} disabled={submitting} data-testid="mq-submit">
                {submitting ? "Sending…" : "Send it to Dorian →"}
              </button>
              <p className="mq-legal">
                Your answers stay with Dorian Reeves and MMG Mortgages. We don't sell your information.
                See our <a href="/privacy">Privacy Policy</a>. A pre-approval is not a guarantee of final financing.
              </p>
            </div>
          )}

          {step === "done" && (
            <div className="mq-handoff">
              {isMobile ? (
                <>
                  <a className="mq-btn mq-cta-primary" href={smsHref} data-testid="mq-cta-text" data-dx-track="handoff_text">Text Dorian →</a>
                  <a className="mq-cta-secondary" href={telHref} data-testid="mq-cta-call" data-dx-track="handoff_call">or tap to call Dorian</a>
                </>
              ) : (
                <>
                  <a className="mq-btn mq-cta-primary" href={telHref} data-testid="mq-cta-call" data-dx-track="handoff_call">Call Dorian →</a>
                  <a className="mq-cta-secondary" href={smsHref} data-testid="mq-cta-text" data-dx-track="handoff_text">or send Dorian a text</a>
                </>
              )}
              <p className="mq-handoff-note">You'll also get a text and an email from me within 1 business day.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
